import { View, Text, styled, useTheme } from 'tamagui'
import { Settings, UserPlus, LogOut, Copy, Calendar, BookOpen } from '@tamagui/lucide-icons'
import { useAppStore, useServerStore } from '@/store'
import { useEffect, useRef } from 'react'
import { TierBadge } from '../../common'
import { TIER_CONFIGS } from '@/mocks'

const Dropdown = styled(View, {
  backgroundColor: '$surfaceRaised',
  borderRadius: '$borderRadiusMd',
  borderWidth: 1,
  borderColor: '$borderDefault',
  paddingVertical: 4,
})

const MenuItem = styled(View, {
  flexDirection: 'row',
  alignItems: 'center',
  height: 32,
  paddingHorizontal: 12,
  gap: 8,
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: '$sidebarHover',
  },
  // @ts-expect-error -- web transition
  style: { transition: 'background-color 100ms ease' },
})

const MenuLabel = styled(Text, {
  fontFamily: '$fontBody',
  fontSize: 13,
  color: '$surfaceForeground',
  flex: 1,
})

const MenuSeparator = styled(View, {
  height: 1,
  backgroundColor: '$borderDefault',
  marginVertical: 4,
  marginHorizontal: 8,
})

interface ServerHeaderDropdownProps {
  serverId: string
  onClose: () => void
}

export function ServerHeaderDropdown({ serverId, onClose }: ServerHeaderDropdownProps) {
  const theme = useTheme()
  const { navigateToServerSettings, openJoinServerModal, toggleScheduledEvents, setWelcomeScreenServerId } = useAppStore()
  const removeServer = useServerStore((s) => s.removeServer)
  const server = useServerStore((s) => s.servers[serverId])
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose()
      }
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    // Delay listener to avoid immediate close
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick)
      document.addEventListener('keydown', handleKeyDown)
    }, 0)
    return () => {
      clearTimeout(timer)
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [onClose])

  return (
    <Dropdown
      ref={ref as any}
      // @ts-expect-error -- web box-shadow
      style={{ boxShadow: theme.shadowCard?.val || 'none' }}
    >
      {server && (
        <View flexDirection="row" alignItems="center" gap={8} paddingHorizontal={12} paddingVertical={6}>
          <TierBadge tier={server.tier} size="sm" />
          <Text fontFamily="$fontBody" fontSize={12} color="$sidebarMuted">
            {TIER_CONFIGS[server.tier].label} Plan{TIER_CONFIGS[server.tier].price > 0 ? ` · $${TIER_CONFIGS[server.tier].price}/mo` : ''}
          </Text>
        </View>
      )}
      <MenuSeparator />
      <MenuItem onPress={() => {
        navigateToServerSettings(serverId)
        onClose()
      }}>
        <Settings size={14} color="$sidebarMuted" />
        <MenuLabel>Server Settings</MenuLabel>
      </MenuItem>

      <MenuItem onPress={() => {
        openJoinServerModal()
        onClose()
      }}>
        <UserPlus size={14} color="$sidebarMuted" />
        <MenuLabel>Create Invite</MenuLabel>
      </MenuItem>

      <MenuItem onPress={() => {
        toggleScheduledEvents()
        onClose()
      }}>
        <Calendar size={14} color="$sidebarMuted" />
        <MenuLabel>Scheduled Events</MenuLabel>
      </MenuItem>

      {server?.welcomeScreen && (
        <MenuItem onPress={() => {
          setWelcomeScreenServerId(serverId)
          onClose()
        }}>
          <BookOpen size={14} color="$sidebarMuted" />
          <MenuLabel>Welcome Screen</MenuLabel>
        </MenuItem>
      )}

      <MenuSeparator />

      <MenuItem onPress={() => {
        removeServer(serverId)
        onClose()
      }}>
        <LogOut size={14} color="$error" />
        <MenuLabel color="$error">Leave Server</MenuLabel>
      </MenuItem>
    </Dropdown>
  )
}
